// ── AI Model constants (frontend mirror of _shared/models.ts) ──────
// SYRA v14 (Sprint A-07): Anthropic Claude / OpenAI GPT-5 são primários
// para rotas factuais e procedurais. Gemini é fallback via Lovable AI.

export const AI_MODELS = {
  // v17 A9-02: Anthropic primário em FAST (Haiku 4.5)
  FAST: "anthropic/claude-haiku-4-5",
  // v17 A9-02: Sonnet 4.6 como default balanced (alinhado com BYOK)
  BALANCED: "anthropic/claude-sonnet-4-6",
  // Raciocínio crítico
  REASONING: "anthropic/claude-sonnet-4-6",
  // Multimodal — Claude Sonnet 4.6 visão nativa
  MULTIMODAL: "anthropic/claude-sonnet-4-6",
  // Verifier
  VERIFIER: "openai/gpt-5-mini",
  // Fallbacks (último recurso)
  FALLBACK_FAST: "openai/gpt-5-nano",
  FALLBACK_BALANCED: "openai/gpt-5-mini",
  FALLBACK_REASONING: "google/gemini-2.5-pro",
} as const;

export type ModelTier = "fast" | "balanced" | "reasoning";
export type ModelSource = "lovable" | "byok";

export interface ModelMeta {
  id: string;
  label: string;
  tier: ModelTier;
  provider: string;
  source: ModelSource;
  /** Theoretical relative score 0-100 — used by auto-tune when no usage data exists. */
  theoretical_score: number;
  /** Average estimated cost per 1k output tokens (USD). */
  est_cost_per_1k?: number;
}

/** Models served via the Lovable AI Gateway (no BYOK required). */
export const LOVABLE_MODELS: ModelMeta[] = [
  { id: "google/gemini-2.5-flash-lite", label: "Gemini 2.5 Flash Lite", tier: "fast", provider: "google", source: "lovable", theoretical_score: 70, est_cost_per_1k: 0.0001 },
  { id: "google/gemini-3-flash-preview", label: "Gemini 3 Flash Preview", tier: "balanced", provider: "google", source: "lovable", theoretical_score: 82, est_cost_per_1k: 0.0003 },
  { id: "google/gemini-2.5-flash", label: "Gemini 2.5 Flash", tier: "balanced", provider: "google", source: "lovable", theoretical_score: 80, est_cost_per_1k: 0.0003 },
  { id: "google/gemini-2.5-pro", label: "Gemini 2.5 Pro", tier: "reasoning", provider: "google", source: "lovable", theoretical_score: 92, est_cost_per_1k: 0.0050 },
  { id: "google/gemini-3.1-pro-preview", label: "Gemini 3.1 Pro Preview", tier: "reasoning", provider: "google", source: "lovable", theoretical_score: 94, est_cost_per_1k: 0.0060 },
  { id: "openai/gpt-5", label: "GPT-5", tier: "reasoning", provider: "openai", source: "lovable", theoretical_score: 95, est_cost_per_1k: 0.0080 },
  { id: "openai/gpt-5-mini", label: "GPT-5 Mini", tier: "balanced", provider: "openai", source: "lovable", theoretical_score: 83, est_cost_per_1k: 0.0008 },
  { id: "openai/gpt-5-nano", label: "GPT-5 Nano", tier: "fast", provider: "openai", source: "lovable", theoretical_score: 72, est_cost_per_1k: 0.0002 },
  { id: "openai/gpt-5.2", label: "GPT-5.2 (último)", tier: "reasoning", provider: "openai", source: "lovable", theoretical_score: 96, est_cost_per_1k: 0.0090 },
];

/** Models commonly available via BYOK providers. Auto-merged with active providers from DB. */
export const BYOK_MODELS_CATALOG: ModelMeta[] = [
  { id: "anthropic/claude-opus-4-7", label: "Claude Opus 4.7", tier: "reasoning", provider: "anthropic", source: "byok", theoretical_score: 96, est_cost_per_1k: 0.0250 },
  { id: "anthropic/claude-sonnet-4-6", label: "Claude Sonnet 4.6", tier: "reasoning", provider: "anthropic", source: "byok", theoretical_score: 94, est_cost_per_1k: 0.0150 },
  { id: "anthropic/claude-haiku-4-5", label: "Claude Haiku 4.5", tier: "fast", provider: "anthropic", source: "byok", theoretical_score: 78, est_cost_per_1k: 0.0050 },
];

/** Legacy export (string-only) kept for backward compatibility. */
export const VALID_MODELS = [...LOVABLE_MODELS, ...BYOK_MODELS_CATALOG].map((m) => ({
  id: m.id,
  label: `${m.label} (${m.tier})`,
  tier: m.tier,
}));

export const DEFAULT_AGENT_MODEL = AI_MODELS.BALANCED;

// ── v14 Fallback chains (used by model-gateway when primary fails) ──
export const FALLBACK_CHAINS: Record<string, string[]> = {
  // v17 A9-02: Anthropic → OpenAI → Gemini (sempre nessa ordem)
  "anthropic/claude-haiku-4-5": ["openai/gpt-5-nano", "openai/gpt-5-mini", "google/gemini-2.5-flash-lite"],
  "anthropic/claude-sonnet-4-6": ["anthropic/claude-opus-4-7", "openai/gpt-5", "google/gemini-2.5-pro"],
  "openai/gpt-5-nano": ["anthropic/claude-haiku-4-5", "google/gemini-2.5-flash-lite"],
  "openai/gpt-5-mini": ["anthropic/claude-haiku-4-5", "google/gemini-3-flash-preview"],
  "openai/gpt-5": ["anthropic/claude-sonnet-4-6", "google/gemini-2.5-pro"],
};
